import { apiFetch } from './client';

/* ── Types ── */

export interface AuthTokenPayload {
  token: string;
  refreshToken: string;
  expiresAt: number; // epoch ms
}

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  orgId: string;
  orgName: string;
  persona: string;
  roles: string[];
  divisions: string[];
  tier: string;
  creditsRemaining: number;
}

/**
 * Wire-format user shape returned by `/auth/refresh` — matches the
 * backend's `userResponse()` (snake_case, with `org_id`/`credits_remaining`
 * and the parsed `vertical_profile`). The AuthGuard maps this to the
 * camelCase store shape (`AuthUser`/`User`).
 */
export interface RefreshUserShape {
  id: string;
  email: string;
  name: string;
  title?: string;
  org_id: string;
  org_name: string;
  persona: string;
  roles: string[];
  divisions: string[];
  tier: string;
  credits_remaining: number;
  needs_onboarding?: boolean;
  user_class?: string | null;
  agency_level?: string | null;
  primary_verticals?: string[];
  state_territory?: string | null;
  vertical_profile?: Record<string, unknown> | null;
}

export interface LoginResponse {
  redirectUrl: string;
}

export interface CallbackResponse {
  auth: AuthTokenPayload;
  user: AuthUser;
}

export interface OnboardData {
  role: string;
  state: string;
  district?: string;
  orgType: string;
}

/**
 * Wire shape from `POST /auth/refresh`. The backend re-reads the user
 * record from BigQuery on every refresh so `roles`, `tier`, `persona`,
 * `onboarded` etc. always reflect the current DB state — no more stale
 * claims from a JWT minted at first-login.
 *
 * `user` may be `null` if the user record was deleted between login
 * and refresh; the FE clears the local cache when it sees null.
 */
export interface RefreshResponse {
  token: string;
  user: RefreshUserShape | null;
}

/**
 * Map the snake_case wire-format from `/auth/refresh` to the camelCase
 * shape the auth store + components consume. Used by AuthGuard and
 * useAuth so both apply server-side role / tier / credit changes
 * consistently.
 */
export function mapRefreshUser(u: RefreshUserShape): AuthUser & {
  userClass?: string;
  agencyLevel?: string;
  primaryVerticals?: string[];
  stateTerritory?: string;
  verticalProfile?: Record<string, unknown>;
} {
  return {
    id: u.id,
    email: u.email,
    name: u.name,
    orgId: u.org_id,
    orgName: u.org_name ?? '',
    persona: u.persona,
    roles: Array.isArray(u.roles) ? u.roles : [],
    divisions: Array.isArray(u.divisions) ? u.divisions : [],
    tier: u.tier,
    creditsRemaining: u.credits_remaining ?? 0,
    userClass: u.user_class ?? undefined,
    agencyLevel: u.agency_level ?? undefined,
    primaryVerticals: u.primary_verticals ?? undefined,
    stateTerritory: u.state_territory ?? undefined,
    verticalProfile: u.vertical_profile ?? undefined,
  };
}

/* ── API calls ── */

/**
 * Initiate SSO login. Returns a redirect URL for the chosen provider.
 */
export function login(provider: 'google' | 'saml'): Promise<LoginResponse> {
  return apiFetch<LoginResponse>('/api/v2/auth/login', {
    method: 'POST',
    body: JSON.stringify({ provider }),
  });
}

/**
 * Exchange OAuth callback code for session tokens.
 */
export function callback(
  code: string,
  state: string,
): Promise<CallbackResponse> {
  return apiFetch<CallbackResponse>('/api/v2/auth/callback', {
    method: 'POST',
    body: JSON.stringify({ code, state }),
  });
}

/**
 * Submit onboarding profile data after account creation.
 */
export function onboard(data: OnboardData): Promise<CallbackResponse> {
  return apiFetch<CallbackResponse>('/api/v2/auth/onboard', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Refresh the current session token.
 */
export function refresh(): Promise<RefreshResponse> {
  return apiFetch<RefreshResponse>('/api/v2/auth/refresh', {
    method: 'POST',
  });
}
